import React, { useCallback, useEffect, useState } from "react";
import "./PaymentModal.scss";
import Helpers from "@utils/Helpers";
import { Modal, Input, Row, Col, Checkbox, Select, Tooltip } from "antd";
import Validates from "@utils/Validates";
import { errorLog } from "logging";
import { paymentApi } from "services/PaymentApi";
import Cards from "react-credit-cards";
import { useUser } from "contexts/UserContext";
import visa from "@assets/icons/visa.svg";
import mastercard from "@assets/icons/mastercard.svg";
import amex from "@assets/icons/american-express.svg";
import paypal from "@assets/icons/paypalRec.svg";
import debit from "@assets/icons/debit.svg";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import Suggestion from "@components/Suggestion/Suggestion";
import { InfoCircleOutlined } from "@ant-design/icons";
const { Option } = Select;

const cardIcons: Record<string, string> = {
  visa,
  mastercard,
  amex,
  paypal,
};

let debounceTimer: any;

interface IPaymentModal {
  isOpenNewCard: boolean;
  setIsOpenNewCard: (value: boolean) => void;
  setAllPayment: (allPayment: any) => void;
}

const PaymentModal: React.FC<IPaymentModal> = ({
  isOpenNewCard,
  setIsOpenNewCard,
  setAllPayment,
}) => {
  const [isOpenSuggestion, setIsOpenSuggestion] = useState(false);
  const [searchResult, setSearchResult] = useState<any[]>([]);
  const { userProfile, userAddresses } = useUser();
  const [isAdding, setIsAdding] = useState(false);
  const [cardDefaultAddress, setCardDefaultAddress] = useState(
    userAddresses ? userAddresses[0]?.address_id : ""
  );
  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: "",
    type: "",
    isDefault: false,
    address: {
      first_name: "",
      last_name: "",
      phone: "",
      street: "",
      apt_unit: "",
      city: "",
      province: "",
      postal_code: "",
      is_primary: false,
    },
  });

  const handleCardChange = (value: any, type: string) => {
    if (type === "number" || type === "cvc" || type === "expiry") {
      value = Validates.validateNumberForm(value);
      if (type === "expiry" && value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4);
        if (value.slice(0, 2) > 12) {
          value = "12" + value.slice(2, 5);
        }
      }
      if (type === "cvc" && value.length > 3) {
        return;
      }
    }
    if (type === "name") {
      value = Validates.validateTextForm(value);
    }

    setCardData({ ...cardData, [type]: value });
  };

  const handleSaveCard = async () => {
    const body = {
      card_number: cardData.number || "",
      cardholder_name: cardData.name || "",
      expiration_date: cardData.expiry || "",
      payment_type: "credit card",
      card_type: cardData.type || "",
      is_default: cardData.isDefault || false,
      cvv: parseInt(cardData.cvc) || 0,
      address: {
        first_name: cardData.address.first_name || "",
        last_name: cardData.address.last_name || "",
        phone: cardData.address.phone || "",
        street: cardData.address.street || "",
        apt_unit: cardData.address.apt_unit || "",
        city: cardData.address.city || "",
        province: cardData.address.province || "",
        postal_code: cardData.address.postal_code || "",
        is_primary: cardData.address.is_primary || false,
      },
    };

    if (
      cardData.number === "" ||
      cardData.name === "" ||
      cardData.expiry === "" ||
      cardData.cvc === ""
    ) {
      Helpers.notification.warning("Please fill in all card information");
      return;
    }

    if (
      cardData.address.first_name === "" ||
      cardData.address.last_name === "" ||
      cardData.address.street === "" ||
      cardData.address.apt_unit == null ||
      cardData.address.city === "" ||
      cardData.address.province === "" ||
      cardData.address.postal_code === ""
    ) {
      Helpers.notification.warning("Please fill in all address information");
      return;
    }
    setIsAdding(true);
    try {
      const response = await paymentApi.createPayment(body);
      if (response.status === 201) {
        setAllPayment(null);
        setIsOpenNewCard(false);
        Helpers.showSuccessUpdated("Card added successfully");
      }
    } catch (error: any) {
      if (error.response.status === 400) {
        Helpers.notification.error(error.response.data.message);
      } else Helpers.notification.error("Cannot add card. Please try again");
      errorLog("Card.tsx - handleSaveCard", error);
    }
    setIsAdding(false);
  };

  const getUserAddress = useCallback(() => {
    if (!cardDefaultAddress) return;
    const address = userAddresses?.find(
      (item: any) => item.address_id === cardDefaultAddress
    );
    return address;
  }, [cardDefaultAddress]);

  useEffect(() => {
    setCardData({
      ...cardData,
      address: {
        first_name: userProfile?.first_name || "",
        last_name: userProfile?.last_name || "",
        phone: userProfile?.phone || "",
        street: getUserAddress()?.street || "",
        apt_unit: getUserAddress()?.aparment || "",
        city: getUserAddress()?.city || "",
        province: getUserAddress()?.province || "",
        postal_code: getUserAddress()?.postal_code || "",
        is_primary: false,
      },
    });
  }, [cardDefaultAddress]);

  return (
    <Modal
      className="PaymentModal"
      title="Add New Card"
      open={isOpenNewCard}
      onOk={handleSaveCard}
      onCancel={() => {
        setIsOpenNewCard(false);
        setCardData({
          number: "",
          expiry: "",
          cvc: "",
          name: "",
          type: "",
          isDefault: false,
          address: {
            first_name: userProfile?.first_name || "",
            last_name: userProfile?.last_name || "",
            phone: "",
            street: userAddresses?.[0]?.street || "",
            apt_unit: userAddresses?.[0]?.aparment || "",
            city: userAddresses?.[0]?.city || "",
            province: userAddresses?.[0]?.province || "",
            postal_code: userAddresses?.[0]?.postal_code || "",
            is_primary: false,
          },
        });
      }}
      okText="Save Card"
      cancelButtonProps={{ type: "text" }}
      okButtonProps={{ disabled: isAdding }}
    >
      <div className="form-group">
        <div className="title">
          Card Number <span className="text-danger">*</span>
        </div>
        <Cards
          number={cardData.number}
          expiry={cardData.expiry}
          cvc={cardData.cvc}
          name={cardData.name}
          callback={(type: any) =>
            setCardData({ ...cardData, type: type.issuer })
          }
        />
        <Input
          size="large"
          variant="filled"
          maxLength={16}
          value={cardData.number}
          suffix={
            cardData.type !== "" && cardData.type !== "unknown" ? (
              <img
                src={cardIcons[cardData.type]}
                alt={""}
                width={20}
                style={{
                  opacity: cardData.type ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />
            ) : (
              <img
                src={debit}
                alt={""}
                width={20}
                style={{
                  opacity: cardData.type ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />
            )
          }
          onChange={(e) => handleCardChange(e.target.value, "number")}
        />
      </div>
      <div className="form-group">
        <div className="title">
          Cardholder's Name <span className="text-danger">*</span>
        </div>
        <Input
          size="large"
          variant="filled"
          value={cardData.name}
          onChange={(e) => handleCardChange(e.target.value, "name")}
        />
      </div>
      <Row gutter={12}>
        <Col span={12}>
          <div className="form-group">
            <div className="title">
              Expiry <span className="text-danger">*</span>
            </div>
            <Input
              size="large"
              variant="filled"
              value={cardData.expiry}
              placeholder="MM/YY"
              maxLength={5}
              onChange={(e) => handleCardChange(e.target.value, "expiry")}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="form-group">
            <div className="title">
              CVV <span className="text-danger">*</span>{" "}
              <Tooltip
                placement="right"
                title={"3 digits on the back of your card"}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <Input.Password
              size="large"
              variant="filled"
              value={cardData.cvc}
              onChange={(e) => handleCardChange(e.target.value, "cvc")}
            />
          </div>
        </Col>
      </Row>
      <Checkbox
        onChange={() =>
          setCardData({
            ...cardData,
            isDefault: !cardData.isDefault,
          })
        }
      >
        Set as default method
      </Checkbox>

      <div className="billing-address">
        <div className="section-title">Billing Address</div>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                First name <span className="text-danger">*</span>
              </div>
              <Input
                variant="filled"
                size="large"
                value={cardData.address.first_name}
                onChange={(e) =>
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      first_name: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                Last name <span className="text-danger">*</span>
              </div>
              <Input
                variant="filled"
                size="large"
                value={cardData.address.last_name}
                onChange={(e) =>
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      last_name: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Col>
        </Row>
        <div className="form-group">
          <div className="title">Phone</div>
          <PhoneInput
            placeholder="Enter phone number"
            defaultCountry="CA"
            value={cardData.address.phone}
            onChange={(e: any) =>
              setCardData({
                ...cardData,
                address: {
                  ...cardData.address,
                  phone: e ?? "",
                },
              })
            }
          />
        </div>
        <br />
        <div className="form-group">
          <Select
            className="w-100"
            size="large"
            value={cardDefaultAddress}
            onChange={(value) => setCardDefaultAddress(value)}
          >
            {userAddresses?.map((item: any, index: number) => (
              <Option value={item.address_id} key={index}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
        <br />
        <div className="form-group map-search-input">
          <div className="title">
            Street Address<span className="text-danger">*</span>
          </div>
          <Input
            size="large"
            variant="filled"
            value={cardData.address.street}
            onChange={(e) => {
              const query = e.target.value;
              setIsOpenSuggestion(true);
              if (query == "") {
                setIsOpenSuggestion(false);
              }
              clearTimeout(debounceTimer);
              debounceTimer = setTimeout(async () => {
                const result = await Helpers.handleSearchLocation(query);
                setSearchResult(result);
              }, 300);
              setCardData({
                ...cardData,
                address: { ...cardData.address, street: query },
              });
            }}
          />
          {isOpenSuggestion && (
            <Suggestion searchResult={searchResult} onSelect={() => {}} />
          )}
        </div>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Apt, Suite, etc</div>
              <Input
                size="large"
                variant="filled"
                value={cardData.address.apt_unit}
                onChange={(e) =>
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      apt_unit: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                City/Town/County <span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={cardData.address.city}
                onChange={(e) => {
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      city: e.target.value,
                    },
                  });
                }}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                Province<span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={cardData.address.province}
                onChange={(e) =>
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      province: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                Postal/Zip Code
                <span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={cardData.address.postal_code}
                onChange={(e) =>
                  setCardData({
                    ...cardData,
                    address: {
                      ...cardData.address,
                      postal_code: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};
export default PaymentModal;
